/**
 * EventPipelineFlow — event-ingest lifecycle, drawn with the shared flowEngine (parallel to the
 * Message pipeline). Traced flow: Event Sources → Event API (/v2/save · x-event-key · validate) →
 * { direct async insert | Events Queue (clickhouse-events) → CH Worker (batch) } → ClickHouse
 * (xnotify.events) → contact-enrichment crons.
 */
import React from "react";
import { Box, Flex, HStack, Text, Icon } from "@chakra-ui/react";
import { WarningTwoIcon, CheckCircleIcon } from "@chakra-ui/icons";
import { TONE, fmtInt } from "./helpers";
import FlowDiagram, { makeRouters } from "./flowEngine";

const BASE_W = 1240, H = 324;

const L0 = {
  sources:        { x: 16,   y: 66, w: 156, h: 104, kind: "external" },
  event_api:      { x: 268,  y: 60, w: 184, h: 116, kind: "service" },
  events_queue:   { x: 524,  y: 63, w: 160, h: 110, kind: "buffer" },
  ch_worker:      { x: 752,  y: 60, w: 184, h: 116, kind: "service" },
  clickhouse:     { x: 1024, y: 60, w: 200, h: 116, kind: "service" }, // machine — CPU/RAM/Disk bars
  contact_enrich: { x: 1024, y: 230, w: 200, h: 84, kind: "service" },
};

function buildEdges(L, flow) {
  const R = makeRouters(L, flow);
  const { seg, mk, topMid, botMid } = R;
  // direct async-insert bypass — arcs ABOVE the main row from Event API into ClickHouse (dashed).
  const eT = topMid("event_api"), cT = topMid("clickhouse"), rail = 26;
  const direct = mk("event_api", "clickhouse", seg(eT, [eT[0], rail], [cT[0], rail], cT), (eT[0] + cT[0]) / 2, rail - 6, { dashed: true });
  // downstream enrichment cron reads new events (dashed, below ClickHouse).
  const cB = botMid("clickhouse"), enT = topMid("contact_enrich");
  const enrich = mk("clickhouse", "contact_enrich", seg(cB, enT), cB[0] + 12, (cB[1] + enT[1]) / 2, { dashed: true, lpos: "right" });
  return [
    R.Hz("sources", "event_api"),
    R.Hz("event_api", "events_queue"),
    R.Hz("events_queue", "ch_worker"),
    R.Hz("ch_worker", "clickhouse"),
    direct,
    enrich,
  ];
}

const BAND_LABELS = [{ text: "Ingest → validate → queue → ClickHouse", x: 16, y: 14 }];

export default function EventPipelineFlow({ pipeline }) {
  const bn = pipeline?.bottleneck;
  return (
    <Box mb={5}>
      {bn && <EventVerdict bn={bn} />}
      <FlowDiagram layout={L0} buildEdges={buildEdges} pipeline={pipeline} baseW={BASE_W} height={H}
        title="Event ingest lifecycle" caption="particles = events in flight · speed ∝ ingest rate" bandLabels={BAND_LABELS} />
    </Box>
  );
}

function EventVerdict({ bn }) {
  const healthy = bn.healthy, t = TONE[healthy ? "ok" : "crit"], ev = bn.evidence || {};
  const chips = [];
  if (ev.ingest_tps != null) chips.push(["ingest", `${ev.ingest_tps}/s`, false]);
  if (ev.queue_depth != null) chips.push(["queue", fmtInt(ev.queue_depth), !healthy && bn.stage === "events_queue"]);
  if (ev.api_cpu != null) chips.push(["API CPU", `${ev.api_cpu}%`, ev.api_cpu >= 85]);
  if (ev.worker_cpu != null) chips.push(["worker CPU", `${ev.worker_cpu}%`, ev.worker_cpu >= 85]);
  if (ev.clickhouse != null) chips.push(["ClickHouse", ev.clickhouse, ev.clickhouse !== "up"]);
  const stageName = { event_api: "Event API", events_queue: "Events Queue", ch_worker: "CH Worker", clickhouse: "ClickHouse" }[bn.stage];
  return (
    <Flex align="center" gap={4} wrap="wrap" mb={3} p={4} borderRadius="xl" bg={healthy ? TONE.ok.bg : TONE.crit.bg} borderWidth="1px" borderColor={healthy ? "green.200" : "red.200"} borderLeftWidth="4px" borderLeftColor={t.dot}>
      <Flex w="38px" h="38px" flex="none" borderRadius="10px" align="center" justify="center" bg="white" color={t.dot} borderWidth="1px" borderColor={healthy ? "green.200" : "red.200"}><Icon as={healthy ? CheckCircleIcon : WarningTwoIcon} boxSize={4} /></Flex>
      <Box flex="1 1 300px" minW="0">
        <Text fontSize="10px" fontWeight="700" letterSpacing="0.12em" textTransform="uppercase" color={t.fg}>{healthy ? "Ingest healthy" : "Ingest bottleneck"}</Text>
        <Text fontWeight="700" fontSize="15px" color="#111827" mt="1px">{healthy ? "Events keeping up — queue drains, ClickHouse writing" : <><Text as="span" color={t.fg}>{stageName}</Text> — {bn.reason}</>}</Text>
      </Box>
      <HStack spacing={2} wrap="wrap">{chips.map(([k, v, hot]) => (
        <HStack key={k} spacing={2} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="8px" px={2.5} py={1.5}>
          <Box w="6px" h="6px" borderRadius="full" bg={hot ? TONE.crit.dot : TONE.ok.dot} /><Text fontSize="11px" color="gray.500">{k}</Text>
          <Text fontSize="12px" fontFamily="mono" fontWeight="700" color={hot ? TONE.crit.fg : "#111827"}>{v}</Text>
        </HStack>))}
      </HStack>
    </Flex>
  );
}
